import { jwtDecode } from 'jwt-decode';
import { STORAGE_KEYS } from '@/common/common-enum';

/**
 * 토큰 검증 상수
 */
const TOKEN_VALIDATION = {
  INVALID_ROLE_PATTERNS: [null, undefined, '', 'null', 'undefined'],
  ERROR_TYPES: {
    EXPIRED: 'expired',
    INVALID_FORMAT: 'invalid_format',
    MISSING_CLAIMS: 'missing_claims',
    INVALID_ROLE: 'invalid_role',
    PROCESSING_ERROR: 'processing_error'
  },
  USER_MESSAGES: {
    expired: '로그인 세션이 만료되었습니다. 다시 로그인해 주세요.',
    invalid_format: '잘못된 로그인 링크입니다. 새로운 링크를 요청해 주세요.',
    missing_claims: '로그인 정보가 불완전합니다. 다시 로그인을 시도해 주세요.',
    invalid_role: '사용자 권한이 유효하지 않습니다. 고객지원에 문의해 주세요.',
    processing_error: '로그인 처리 중 오류가 발생했습니다. 다시 시도해 주세요.'
  }
};

/**
 * 개발 환경에서만 로그를 출력하는 유틸리티
 */
const devLog = {
  info: (message, data) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(message, data);
    }
  },
  warn: (message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(message, error);
    } else {
      console.warn(message);
    }
  },
  error: (message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    } else {
      console.error(message);
    }
  }
};

/**
 * URL 쿼리 파라미터에서 JWT 토큰을 처리합니다.
 * 토큰을 디코딩하고, 검증한 후 사용자 정보를 생성하여 localStorage에 저장하고 Auth Store를 업데이트합니다.
 * @param {string} token - URL에서 추출한 JWT 토큰
 * @returns {Promise<{success: boolean, errorType?: string, message?: string}>} - 처리 결과
 */
export async function processTokenFromUrl(token) {
  try {
    validateTokenFormat(token);
    const decoded = jwtDecode(token);
    
    // 개발 환경에서 디버깅 정보 출력
    devLog.info('Decoded token:', {
      sub: decoded.sub,
      role: decoded.role,
      exp: decoded.exp ? new Date(decoded.exp * 1000) : 'No exp',
      iat: decoded.iat ? new Date(decoded.iat * 1000) : 'No iat'
    });
    
    validateTokenClaims(decoded);
    
    const user = createUserObject(decoded, token);
    
    // localStorage 저장
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    
    // Auth Store 상태 업데이트 (동적 import로 순환 참조 방지)
    await updateAuthStore(user);
    
    return { success: true };
  } catch (e) {
    return handleTokenError(e);
  }
}

/**
 * Auth Store 상태를 업데이트합니다.
 */
async function updateAuthStore(user) {
  try {
    const { useAuthStore } = await import('@/store/auth.module.js');
    const authStore = useAuthStore();
    authStore.snsLogin(user);
    
    devLog.info('Auth store updated with user:', user);
  } catch (error) {
    devLog.warn('Failed to update auth store:', error);
  }
}

function validateTokenFormat(token) {
  if (!token || typeof token !== 'string' || token.trim().length === 0) {
    const error = new Error('Invalid token format');
    error.type = TOKEN_VALIDATION.ERROR_TYPES.INVALID_FORMAT;
    throw error;
  }
}

function validateTokenClaims(decoded) {
  const { ERROR_TYPES, INVALID_ROLE_PATTERNS } = TOKEN_VALIDATION;
  
  // 토큰 만료 시간 검증
  if (!decoded.exp || decoded.exp * 1000 < Date.now()) {
    const error = new Error('Token expired');
    error.type = ERROR_TYPES.EXPIRED;
    throw error;
  }
  
  // 필수 클레임 검증
  if (!decoded.sub || !decoded.role) {
    const error = new Error('Invalid token structure: missing required claims');
    error.type = ERROR_TYPES.MISSING_CLAIMS;
    throw error;
  }

  // 역할 유효성 검증
  if (INVALID_ROLE_PATTERNS.includes(decoded.role) || typeof decoded.role !== 'string') {
    const error = new Error('Invalid role in token');
    error.type = ERROR_TYPES.INVALID_ROLE;
    throw error;
  }
}

function createUserObject(decoded, token) {
  return {
    accountId: decoded.sub,
    roleType: decoded.role,
    userRoleType: decoded.role, // 기존 호환성 유지
    accessToken: token,
  };
}

function handleTokenError(error) {
  devLog.error("Token validation failed:", error);
  
  // 에러 타입에 따른 구체적인 사용자 메시지 반환
  const errorType = error.type || TOKEN_VALIDATION.ERROR_TYPES.PROCESSING_ERROR;
  const userMessage = TOKEN_VALIDATION.USER_MESSAGES[errorType] || TOKEN_VALIDATION.USER_MESSAGES.processing_error;
  
  devLog.warn(userMessage);
  
  return {
    success: false,
    errorType,
    message: userMessage
  };
}
